/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import type { ICellRendererParams } from 'ag-grid-community'
import colors from '@sportninja/common/constants/appColors'
import type { TeamFinderRow } from '@sportninja/common/features/teamFinder/resultRow'
import { font, weightSemiBold, paragraphSmall } from 'src/components/css'
import { linkCellStyle } from './linkCellStyle'

/**
 * Team logo + name, linking to the team page — the table's echo of the card's
 * avatar and title. Opens in a new tab for the same reason the card's link
 * does: a search is expensive to re-run, and navigating in place throws away
 * the results and the paged-in extras behind them.
 */
const TeamNameCell = ({ data }: ICellRendererParams<TeamFinderRow>) => {
  // AG Grid renders this cell for its loading / full-width rows, which carry
  // no data.
  if (!data) {
    return null
  }

  const content = (
    <>
      <img css={logoStyle} src={data.teamLogo} alt='' />
      <span css={nameStyle}>{data.teamName}</span>
    </>
  )

  if (!data.teamId) {
    return <span css={wrapStyle}>{content}</span>
  }

  return (
    <a
      css={[wrapStyle, linkCellStyle]}
      href={`/team/${data.teamId}`}
      target='_blank'
      rel='noopener noreferrer'
    >
      {content}
    </a>
  )
}

export default TeamNameCell

// `height: 100%` is what does the centering: AG Grid gives the cell its height,
// and without claiming it the wrapper shrinks to its content and sits on the
// text baseline instead of the row's middle.
const wrapStyle = css`
  display: flex;
  align-items: center;
  height: 100%;
  gap: 8px;
  min-width: 0;
`
const logoStyle = css`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: contain;
  background-color: ${colors.NEUTRAL_0};
  flex: 0 0 auto;
`
const nameStyle = css`
  ${font.body};
  ${paragraphSmall};
  ${weightSemiBold};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
